import React from "react";
import LaunchIcon from "@mui/icons-material/Launch";
import RuleIcon from "@mui/icons-material/Rule";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getGridNumericOperators,
  GridColDef,
  GridFilterOperator,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid-pro";
import { MultiChipCell, SingleChipCell } from "components/RATDataGrid";
import { LinkChipType } from "components/LinkChip";
import { IdNamePair, TableSummary } from "types/tableSummary";
import sanitizeHtml from "sanitize-html";
import { TableSummaryGridAction_Delete } from "./TableSummaryGridAction_Delete";
import { TableSummaryGridAction_Open } from "./TableSummaryGridAction_Open";
import { TableSummaryGridAction_Validate } from "./TableSummaryGridAction_Validate";

const EMPTY_CELL = "";
/** Comments / Sources: ~6 lines at 14px / 1.4 line-height, then scroll. */
const HTML_CELL_MAX_HEIGHT_PX = 120;

export enum TableSummaryGridField {
  Actions = "actions",
  TableId = "tableId",
  CompareId = "compareId",
  TableType = "tableType",
  Rulesets = "rulesets",
  LookupProperties = "lookupProperties",
  DependencyProperties = "dependencyProperties",
  RowCount = "rowCount",
  ValidationErrorCount = "validationErrorCount",
  ValidationWarningCount = "validationWarningCount",
  ValidationInfoCount = "validationInfoCount",
  Comments = "comments",
  Sources = "sources",
  OwnerTool = "ownerTool",
}

export function getColumns(): GridColDef<TableSummary>[] {
  return [
    {
      field: TableSummaryGridField.Actions,
      headerName: "Actions",
      type: "actions",
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      resizable: false,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: "center",
      headerAlign: "center",
      getActions: (params: GridRowParams<TableSummary>) => [
        <TableSummaryGridAction_Open
          key="open"
          label="Open table in new tab"
          icon={<LaunchIcon fontSize="small" />}
          showInMenu
          tableId={params.row.tableId}
        />,
        <TableSummaryGridAction_Validate
          key="validate"
          label="Validate table"
          icon={<RuleIcon fontSize="small" />}
          showInMenu
          tableId={params.row.tableId}
        />,
        <TableSummaryGridAction_Delete
          key="delete"
          label="Delete table"
          icon={<DeleteOutlineIcon fontSize="small" color="warning" />}
          color="warning"
          showInMenu
          closeMenuOnClick={false}
          tableId={params.row.tableId}
          tableType={params.row.tableType}
        />,
      ],
    },
    {
      field: TableSummaryGridField.TableId,
      headerName: "Table ID",
      type: "string",
      minWidth: 200,
      width: 220,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<TableSummary, string>) => (
        <SingleChipCell
          chipType={LinkChipType.TABLE_LINK}
          value={params.value}
        />
      ),
    },
    {
      field: TableSummaryGridField.CompareId,
      headerName: "Compare ID",
      type: "string",
      minWidth: 200,
      width: 220,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => formatOptionalText(row.compareId),
      renderCell: (params: GridRenderCellParams<TableSummary, string>) => {
        const value = params.value || params.row.compareId;
        return value ? <span>{value}</span> : null;
      },
    },
    {
      field: TableSummaryGridField.TableType,
      headerName: "Type",
      type: "string",
      minWidth: 90,
      width: 110,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<TableSummary, string>) => (
        <SingleChipCell
          chipType={LinkChipType.TABLE_TYPE}
          value={params.value}
        />
      ),
    },
    {
      field: TableSummaryGridField.Rulesets,
      headerName: "Rulesets",
      type: "string",
      minWidth: 120,
      width: 200,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) =>
        getCellValue(row, TableSummaryGridField.Rulesets),
      renderCell: (params: GridRenderCellParams<TableSummary>) => (
        <MultiChipCell
          chipType={LinkChipType.RULESET}
          chipItems={params.row.rulesets}
        />
      ),
    },
    {
      field: TableSummaryGridField.LookupProperties,
      headerName: "Lookup Properties",
      type: "string",
      minWidth: 140,
      width: 200,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) =>
        getCellValue(row, TableSummaryGridField.LookupProperties),
      renderCell: (params: GridRenderCellParams<TableSummary>) => (
        <MultiChipCell
          chipType={LinkChipType.PROPERTY_LINK_LOOKUP}
          chipItems={params.row.lookupProperties}
        />
      ),
    },
    {
      field: TableSummaryGridField.DependencyProperties,
      headerName: "Dependency Properties",
      type: "string",
      minWidth: 160,
      width: 200,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) =>
        getCellValue(row, TableSummaryGridField.DependencyProperties),
      renderCell: (params: GridRenderCellParams<TableSummary>) => (
        <MultiChipCell
          chipType={LinkChipType.PROPERTY_LINK}
          chipItems={params.row.dependencyProperties}
        />
      ),
    },
    {
      field: TableSummaryGridField.RowCount,
      headerName: "Rows",
      type: "number",
      minWidth: 70,
      width: 100,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      filterOperators: getGridNumericOperators(),
    },
    {
      field: TableSummaryGridField.ValidationErrorCount,
      headerName: "Errors",
      type: "number",
      minWidth: 90,
      width: 110,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      filterOperators: [
        hasCountOperator("Has Errors", "hasValidationErrors"),
        hasNoCountOperator("Has no Errors", "noValidationErrors"),
        ...getGridNumericOperators(),
      ],
      renderCell: (params: GridRenderCellParams<TableSummary, number>) =>
        renderValidationCountCell(
          params.value,
          LinkChipType.VALIDATION_COUNT_ERROR
        ),
    },
    {
      field: TableSummaryGridField.ValidationWarningCount,
      headerName: "Warnings",
      type: "number",
      minWidth: 100,
      width: 120,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      filterOperators: [
        hasCountOperator("Has Warnings", "hasValidationWarnings"),
        hasNoCountOperator("Has no Warnings", "noValidationWarnings"),
        ...getGridNumericOperators(),
      ],
      renderCell: (params: GridRenderCellParams<TableSummary, number>) =>
        renderValidationCountCell(
          params.value,
          LinkChipType.VALIDATION_COUNT_WARNING
        ),
    },
    {
      field: TableSummaryGridField.ValidationInfoCount,
      headerName: "Info",
      type: "number",
      minWidth: 80,
      width: 100,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      filterOperators: [
        hasCountOperator("Has Info", "hasValidationInfo"),
        hasNoCountOperator("Has no Info", "noValidationInfo"),
        ...getGridNumericOperators(),
      ],
      renderCell: (params: GridRenderCellParams<TableSummary, number>) =>
        renderValidationCountCell(
          params.value,
          LinkChipType.VALIDATION_COUNT_INFO
        ),
    },
    {
      field: TableSummaryGridField.Comments,
      headerName: "Comments",
      type: "string",
      minWidth: 100,
      flex: 1,
      sortable: true,
      filterable: true,
      align: "left",
      headerAlign: "center",
      valueGetter: (_value, row) => formatOptionalText(row.comments),
      renderCell: (params: GridRenderCellParams<TableSummary>) =>
        renderHtmlCell(params.row.comments),
    },
    {
      field: TableSummaryGridField.Sources,
      headerName: "Sources",
      type: "string",
      minWidth: 100,
      flex: 1,
      sortable: true,
      filterable: true,
      align: "left",
      headerAlign: "center",
      valueGetter: (_value, row) => formatOptionalText(row.sources),
      renderCell: (params: GridRenderCellParams<TableSummary>) =>
        renderHtmlCell(params.row.sources),
    },
    {
      field: TableSummaryGridField.OwnerTool,
      headerName: "Owner Tool",
      type: "string",
      minWidth: 100,
      width: 120,
      sortable: true,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => formatOptionalText(row.ownerTool),
    },
  ];
}

/** Scalar value for sort / filter / export (chip columns → joined names). */
function getCellValue(
  summary: TableSummary,
  columnKey: TableSummaryGridField
): string | number {
  switch (columnKey) {
    case TableSummaryGridField.Actions:
      return EMPTY_CELL;
    case TableSummaryGridField.TableId:
      return summary.tableId;
    case TableSummaryGridField.CompareId:
      return formatOptionalText(summary.compareId);
    case TableSummaryGridField.TableType:
      return summary.tableType;
    case TableSummaryGridField.Rulesets:
      return formatIdNamePairs(summary.rulesets);
    case TableSummaryGridField.LookupProperties:
      return formatIdNamePairs(summary.lookupProperties);
    case TableSummaryGridField.DependencyProperties:
      return formatIdNamePairs(summary.dependencyProperties);
    case TableSummaryGridField.RowCount:
      return summary.rowCount;
    case TableSummaryGridField.ValidationErrorCount:
      return summary.validationErrorCount;
    case TableSummaryGridField.ValidationWarningCount:
      return summary.validationWarningCount;
    case TableSummaryGridField.ValidationInfoCount:
      return summary.validationInfoCount;
    case TableSummaryGridField.OwnerTool:
      return formatOptionalText(summary.ownerTool);
    case TableSummaryGridField.Comments:
      return formatOptionalText(summary.comments);
    case TableSummaryGridField.Sources:
      return formatOptionalText(summary.sources);
    default: {
      const _exhaustive: never = columnKey;
      return _exhaustive;
    }
  }
}

function formatIdNamePairs(items: readonly IdNamePair[]): string {
  if (items.length === 0) {
    return EMPTY_CELL;
  }

  return items.map(({ name }) => name).join(", ");
}

function formatOptionalText(value: string | undefined): string {
  return value?.trim() ? value : EMPTY_CELL;
}

/** Render Comments / Sources the same way as the editable CommentsAndSources card. */
function renderHtmlCell(html: string | undefined) {
  const trimmed = html?.trim();
  if (!trimmed) {
    return null;
  }

  return (
    <div
      style={{
        width: "100%",
        maxHeight: HTML_CELL_MAX_HEIGHT_PX,
        overflowX: "hidden",
        overflowY: "auto",
        whiteSpace: "normal",
        fontSize: 14,
        lineHeight: 1.4,
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(trimmed) }}
    />
  );
}

function renderValidationCountCell(
  value: number | null | undefined,
  chipType: LinkChipType
) {
  const count = value ?? 0;
  if (count === 0) {
    return null;
  }

  return <SingleChipCell chipType={chipType} value={String(count)} />;
}

function hasCountOperator(
  label: string,
  value: string
): GridFilterOperator<TableSummary, number> {
  return {
    label,
    value,
    requiresFilterValue: false,
    getApplyFilterFn: () => (cellValue) => (cellValue ?? 0) > 0,
  };
}

function hasNoCountOperator(
  label: string,
  value: string
): GridFilterOperator<TableSummary, number> {
  return {
    label,
    value,
    requiresFilterValue: false,
    getApplyFilterFn: () => (cellValue) => (cellValue ?? 0) === 0,
  };
}
